import React, { useState, useEffect } from "react";

const Security = ({ security = {}, setSecurity }) => {
    const [securitySettings, setSecuritySettings] = useState({
        // Password Policy
        password_min_length: 8,
        password_require_uppercase: true,
        password_require_lowercase: true,
        password_require_numbers: true,
        password_require_special_chars: true,
        password_max_age_days: 90,
        password_history_count: 5,
        password_common_check: true,
        
        // OTP Configuration
        otp_enabled: true,
        otp_length: 6,
        otp_expiry_minutes: 5,
        otp_max_attempts: 3,
        otp_lockout_duration_minutes: 15,
        otp_email_enabled: true,
        otp_sms_enabled: true,
        otp_whatsapp_enabled: false,
        
        // Two-Factor Authentication
        two_factor_enabled: true,
        two_factor_methods: ['email', 'sms'], // 'email', 'sms', 'app', 'backup_codes'
        two_factor_backup_codes_count: 10,
        two_factor_remember_device_days: 30,
        
        // Login Security
        login_max_attempts: 5,
        login_lockout_duration_minutes: 30,
        login_remember_me_days: 30,
        login_ip_whitelist: '',
        login_geolocation_check: false,
        login_device_fingerprinting: true,
        
        // Session Management
        session_timeout_minutes: 60,
        session_max_concurrent: 3,
        session_secure_cookies: true,
        session_same_site: 'strict', // 'strict', 'lax', 'none'
        session_http_only: true,
        
        // Account Security
        account_verification_required: true,
        account_auto_lockout_attempts: 10,
        account_lockout_duration_hours: 24,
        account_password_reset_expiry_hours: 24,
        account_email_change_verification: true,
        account_phone_change_verification: true,
        
        // API Security
        api_rate_limit_per_minute: 100,
        api_key_expiry_days: 365,
        api_ip_whitelist: '',
        api_encryption_required: true,
        api_audit_logging: true,
        
        // Data Protection
        data_encryption_at_rest: true,
        data_encryption_in_transit: true,
        data_anonymization: false,
        data_retention_days: 2555, // 7 years
        data_backup_encryption: true,
        
        // Security Monitoring
        security_logging_enabled: true,
        failed_login_alerts: true,
        suspicious_activity_alerts: true,
        admin_security_notifications: true,
        security_report_frequency: 'daily', // 'hourly', 'daily', 'weekly'
        
        // Compliance
        gdpr_compliance: true,
        hipaa_compliance: false,
        pci_compliance: false,
        audit_trail_retention_days: 2555,
        data_breach_notification_hours: 72,
        
        // Advanced Security
        brute_force_protection: true,
        captcha_enabled: false,
        captcha_site_key: '',
        captcha_secret_key: '',
        honeypot_enabled: true,
        security_headers_enabled: true,
        
        // Email Security
        email_verification_required: true,
        email_verification_expiry_hours: 24,
        email_change_notification: true,
        suspicious_email_alerts: true,
        
        // Mobile Security
        mobile_app_pin_enabled: false,
        mobile_app_biometric_enabled: false,
        mobile_app_timeout_minutes: 15,
        mobile_app_remote_logout: true
    });

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (security && Object.keys(security).length > 0) {
            setSecuritySettings(prev => ({ ...prev, ...security }));
        }
    }, [security]);

    const handleInputChange = (field, value) => {
        const updatedSettings = {
            ...securitySettings,
            [field]: value
        };
        setSecuritySettings(updatedSettings);
        setSecurity(updatedSettings);
    };

    const handleToggleChange = (field) => {
        handleInputChange(field, !securitySettings[field]);
    };


    const SecuritySection = ({ title, children, description }) => (
        <div className="dc-tabsinfo dc-tabsinfo-mt">
            <div className="dc-tabscontenttitle">
                <h3>{title}</h3>
                {description && <p className="text-muted">{description}</p>}
            </div>
            <div className="dc-formtheme dc-userform">
                {children}
            </div>
        </div>
    );

    return (
        <div className="dc-tabscontent tab-content">
            <div className="dc-personalskillshold tab-pane active fade show" id="dc-skills">
                <div className="dc-tabsinfo">
                    <div className="dc-tabscontenttitle">
                        <h3>Security Configuration</h3>
                        <p className="text-muted">Configure comprehensive security settings for your medical platform including OTP, password policies, and authentication.</p>
                    </div>
                </div>

                {/* Password Policy */}
                <SecuritySection
                    title="Password Policy"
                    description="Configure password strength requirements and policies"
                >
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="password_min_length">Minimum Password Length</label>
                                <input
                                    type="number"
                                    id="password_min_length"
                                    value={securitySettings.password_min_length || 8}
                                    onChange={(e) => handleInputChange('password_min_length', parseInt(e.target.value))}
                                    className="form-control"
                                    min="6"
                                    max="32"
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="password_max_age_days">Password Max Age (days)</label>
                                <input
                                    type="number"
                                    id="password_max_age_days"
                                    value={securitySettings.password_max_age_days || 90}
                                    onChange={(e) => handleInputChange('password_max_age_days', parseInt(e.target.value))}
                                    className="form-control"
                                    min="30"
                                    max="365"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="password_require_uppercase">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Require Uppercase Letters
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="password_require_uppercase"
                                        checked={securitySettings.password_require_uppercase || false}
                                        onChange={() => handleToggleChange('password_require_uppercase')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="password_require_lowercase">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Require Lowercase Letters
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="password_require_lowercase"
                                        checked={securitySettings.password_require_lowercase || false}
                                        onChange={() => handleToggleChange('password_require_lowercase')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="password_require_numbers">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Require Numbers
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="password_require_numbers"
                                        checked={securitySettings.password_require_numbers || false}
                                        onChange={() => handleToggleChange('password_require_numbers')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="password_require_special_chars">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Require Special Characters
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="password_require_special_chars"
                                        checked={securitySettings.password_require_special_chars || false}
                                        onChange={() => handleToggleChange('password_require_special_chars')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </SecuritySection>

                {/* OTP Configuration */}
                <SecuritySection
                    title="OTP Configuration"
                    description="Configure One-Time Password settings for authentication"
                >
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                            <label htmlFor="otp_enabled">Enable OTP Authentication</label>
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    
                                    <label htmlFor="otp_enabled">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Enable OTP Authentication
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="otp_enabled"
                                        checked={securitySettings.otp_enabled || false}
                                        onChange={() => handleToggleChange('otp_enabled')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="otp_length">OTP Length</label>
                                <select
                                    id="otp_length"
                                    value={securitySettings.otp_length || 6}
                                    onChange={(e) => handleInputChange('otp_length', parseInt(e.target.value))}
                                    className="form-control"
                                >
                                    <option value="4">4 digits</option>
                                    <option value="6">6 digits</option>
                                    <option value="8">8 digits</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="otp_expiry_minutes">OTP Expiry (minutes)</label>
                                <input
                                    type="number"
                                    id="otp_expiry_minutes"
                                    value={securitySettings.otp_expiry_minutes || 5}
                                    onChange={(e) => handleInputChange('otp_expiry_minutes', parseInt(e.target.value))}
                                    className="form-control"
                                    min="1"
                                    max="60"
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="otp_max_attempts">Max OTP Attempts</label>
                                <input
                                    type="number"
                                    id="otp_max_attempts"
                                    value={securitySettings.otp_max_attempts || 3}
                                    onChange={(e) => handleInputChange('otp_max_attempts', parseInt(e.target.value))}
                                    className="form-control"
                                    min="1"
                                    max="10"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="otp_email_enabled">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Email OTP
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="otp_email_enabled"
                                        checked={securitySettings.otp_email_enabled || false}
                                        onChange={() => handleToggleChange('otp_email_enabled')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="otp_sms_enabled">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            SMS OTP
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="otp_sms_enabled"
                                        checked={securitySettings.otp_sms_enabled || false}
                                        onChange={() => handleToggleChange('otp_sms_enabled')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="otp_whatsapp_enabled">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            WhatsApp OTP
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="otp_whatsapp_enabled"
                                        checked={securitySettings.otp_whatsapp_enabled || false}
                                        onChange={() => handleToggleChange('otp_whatsapp_enabled')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </SecuritySection>

                {/* Login Security */}
                <SecuritySection
                    title="Login Security"
                    description="Configure login attempt limits and security measures"
                >
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="login_max_attempts">Max Login Attempts</label>
                                <input
                                    type="number"
                                    id="login_max_attempts"
                                    value={securitySettings.login_max_attempts || 5}
                                    onChange={(e) => handleInputChange('login_max_attempts', parseInt(e.target.value))}
                                    className="form-control"
                                    min="3"
                                    max="20"
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="login_lockout_duration_minutes">Lockout Duration (minutes)</label>
                                <input
                                    type="number"
                                    id="login_lockout_duration_minutes"
                                    value={securitySettings.login_lockout_duration_minutes || 30}
                                    onChange={(e) => handleInputChange('login_lockout_duration_minutes', parseInt(e.target.value))}
                                    className="form-control"
                                    min="5"
                                    max="1440"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="login_geolocation_check">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Geolocation Check
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="login_geolocation_check"
                                        checked={securitySettings.login_geolocation_check || false}
                                        onChange={() => handleToggleChange('login_geolocation_check')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="login_device_fingerprinting">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Device Fingerprinting
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="login_device_fingerprinting"
                                        checked={securitySettings.login_device_fingerprinting || false}
                                        onChange={() => handleToggleChange('login_device_fingerprinting')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </SecuritySection>

                {/* Session Management */}
                <SecuritySection
                    title="Session Management"
                    description="Configure session timeout and security settings"
                >
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="session_timeout_minutes">Session Timeout (minutes)</label>
                                <input
                                    type="number"
                                    id="session_timeout_minutes"
                                    value={securitySettings.session_timeout_minutes || 60}
                                    onChange={(e) => handleInputChange('session_timeout_minutes', parseInt(e.target.value))}
                                    className="form-control"
                                    min="15"
                                    max="480"
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="session_max_concurrent">Max Concurrent Sessions</label>
                                <input
                                    type="number"
                                    id="session_max_concurrent"
                                    value={securitySettings.session_max_concurrent || 3}
                                    onChange={(e) => handleInputChange('session_max_concurrent', parseInt(e.target.value))}
                                    className="form-control"
                                    min="1"
                                    max="10"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="session_secure_cookies">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Secure Cookies
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="session_secure_cookies"
                                        checked={securitySettings.session_secure_cookies || false}
                                        onChange={() => handleToggleChange('session_secure_cookies')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="session_http_only">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            HTTP Only Cookies
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="session_http_only"
                                        checked={securitySettings.session_http_only || false}
                                        onChange={() => handleToggleChange('session_http_only')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </SecuritySection>

                {/* Security Monitoring */}
                <SecuritySection
                    title="Security Monitoring"
                    description="Configure security alerts and monitoring"
                >
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="security_logging_enabled">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Enable Security Logging
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="security_logging_enabled"
                                        checked={securitySettings.security_logging_enabled || false}
                                        onChange={() => handleToggleChange('security_logging_enabled')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="failed_login_alerts">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Failed Login Alerts
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="failed_login_alerts"
                                        checked={securitySettings.failed_login_alerts || false}
                                        onChange={() => handleToggleChange('failed_login_alerts')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <div className="dc-checkbox-holder" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    marginBottom: '10px'
                                }}>
                                    <label htmlFor="suspicious_activity_alerts">
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            Suspicious Activity Alerts
                                        </span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        id="suspicious_activity_alerts"
                                        checked={securitySettings.suspicious_activity_alerts || false}
                                        onChange={() => handleToggleChange('suspicious_activity_alerts')}
                                        className="dc-checkbox"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="security_report_frequency">Security Report Frequency</label>
                                <select
                                    id="security_report_frequency"
                                    value={securitySettings.security_report_frequency || 'daily'}
                                    onChange={(e) => handleInputChange('security_report_frequency', e.target.value)}
                                    className="form-control"
                                >
                                    <option value="hourly">Hourly</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </SecuritySection>

            </div>
        </div>
    );
};

export default Security;
